import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import map from 'lodash/map';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';

import Banner from 'zignal-landscape/lib/Banner';
import Button from 'zignal-landscape/lib/Button';
import ButtonGroup from 'zignal-landscape/lib/ButtonGroup';
import Col from 'zignal-landscape/lib/Col';
import Field from 'zignal-landscape/lib/Field';
import Grid from 'zignal-landscape/lib/Grid';
import GridContent from 'zignal-landscape/lib/GridContent';
import Heading from 'zignal-landscape/lib/Heading';
import Input from 'zignal-landscape/lib/Input';
import Label from 'zignal-landscape/lib/Label';
import Link from 'zignal-landscape/lib/Link';
import Modal from 'zignal-landscape/lib/Modal';
import ModalHeader from 'zignal-landscape/lib/ModalHeader';
import ModalBody from 'zignal-landscape/lib/ModalBody';
import ModalFooter from 'zignal-landscape/lib/ModalFooter';
import Row from 'zignal-landscape/lib/Row';
import Select from 'zignal-landscape/lib/Select';
import Loading from 'zignal-landscape/lib/Loading';
import * as actions from './NarrativeFeeds.ducks';
import xor from 'lodash/xor';

import styles from './NarrativeFeeds.css';

export const mapStateToProps = state => ({
  // Data
  feeds: state.NarrativeFeeds.feeds,
  newFeedName: state.NarrativeFeeds.newFeedName,
  bannerMessage: state.NarrativeFeeds.bannerMessage,
  bannerTitle: state.NarrativeFeeds.bannerTitle,
  bannerType: state.NarrativeFeeds.bannerType,
  curatedFeeds: state.NarrativeFeeds.curatedFeeds,
  enabledCuratedFeeds: state.NarrativeFeeds.enabledCuratedFeeds,
  campaign: state.NarrativeFeeds.campaign,
  // Flags
  fetchingFeeds: state.NarrativeFeeds.fetchingFeeds,
  creatingFeed: state.NarrativeFeeds.creatingFeed,
  updatingFeed: state.NarrativeFeeds.updatingFeed,
  deletingFeed: state.NarrativeFeeds.deletingFeed,
  fetchingCuratedFeeds: state.NarrativeFeeds.fetchingCuratedFeeds,
  updatingCuratedFeeds: state.NarrativeFeeds.updatingCuratedFeeds,
  fetchingCampaign: state.NarrativeFeeds.fetchingCampaign,
  updatingCampaign: state.NarrativeFeeds.updatingCampaign
});

export const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return Object.assign({}, stateProps, dispatchProps, ownProps);
};

const DEFAULT_CURATED_FEEDS = [];
const DEFAULT_ENABLED_CURATED_FEEDS = [];
const DEFAULT_CAMPAIGN = {};
const DEFAULT_FEEDS = [];

const NarrativeFeeds = ({
  // Props from parent
  accountId,
  campaignId,
  profiles,
  isInternalAdmin,
  // State from Redux
  newFeedName = '',
  bannerMessage = '',
  bannerTitle = '',
  bannerType = '',
  curatedFeeds = DEFAULT_CURATED_FEEDS,
  enabledCuratedFeeds = DEFAULT_ENABLED_CURATED_FEEDS,
  campaign = DEFAULT_CAMPAIGN,
  fetchingFeeds = false,
  deletingFeed = false,
  creatingFeed = false,
  updatingFeed = false,
  fetchingCuratedFeeds = false,
  updatingCuratedFeeds = false,
  fetchingCampaign = false,
  updatingCampaign = false,
  // Action creators
  fetchFeeds,
  createFeed,
  updateFeed,
  deleteFeed,
  fetchCampaign,
  updateCampaign,
  setNewFeedName,
  setBanner,
  updateFeedProfiles,
  updateFeedName,
  fetchCuratedFeeds,
  setEnabledCuratedFeeds,
  feeds = DEFAULT_FEEDS
}) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [feedToDeleteIndex, setFeedToDeleteIndex] = useState(null);
  const [localFeedNames, setLocalFeedNames] = useState({});

  const originalFeedsRef = useRef([]);
  useEffect(() => {
    if (campaignId && accountId) {
      fetchCampaign({ campaignId, accountId });
    }
  }, [fetchCampaign, campaignId, accountId]);

  useEffect(() => {
    if (!fetchingFeeds) {
      fetchFeeds({ accountId, campaignId });
    }
    if (isInternalAdmin && !fetchingCuratedFeeds) {
      fetchCuratedFeeds({ accountId });
    }
  }, [fetchFeeds, fetchCuratedFeeds, accountId, campaignId, isInternalAdmin]);

  useEffect(() => {
    if (feeds.length > 0) {
      originalFeedsRef.current = JSON.parse(JSON.stringify(feeds));
      // Clear local feed names when feeds are loaded
      setLocalFeedNames({});
    }
  }, [fetchingFeeds]);

  useEffect(() => {
    if (originalFeedsRef.current.length !== feeds.length) {
      const feedsAdded = JSON.parse(JSON.stringify(feeds)).filter(
        f => !originalFeedsRef.current.find(of => of.id === f.id)
      );
      originalFeedsRef.current = originalFeedsRef.current
        .filter(f => !feeds.find(of => of.id === f.id))
        .concat(feedsAdded);
    }
  }, []);

  const profileOptions = profiles.map(p => {
    return { label: p.name, value: p.id };
  });

  const onSelectedProfilesChange = feedIndex => selected => {
    const values = map(selected, 'value');
    updateFeedProfiles({ feedIndex, profiles: values });
  };

  const onNameChange = feedIndex => event => {
    updateFeedName({ feedIndex, name: event.target.value });
  };

  const saveCuratedFeeds = () => {
    const curatedFeedIds = enabledCuratedFeeds.map(feed => feed.value);
    // don't overwrite existing timestamps
    const updatedFeeds = curatedFeedIds.map(feedId => {
      const existingEntry = campaign.narrative_feeds.find(f => f.id === feedId);
      return { feedId, timestamp: existingEntry?.timestamp || new Date() };
    });

    const shouldUpdate = updatingCampaign === false && xor(updatedFeeds, campaign.narrative_feeds).length !== 0;
    if (shouldUpdate) {
      updateCampaign({ ...campaign, narrative_feeds: updatedFeeds });
    }
  };

  const handleCreateFeed = () => {
    if (!newFeedName.trim()) {
      return;
    }
    createFeed({ accountId, campaignId, name: newFeedName });
    originalFeedsRef.current.push({ name: newFeedName, profile_ids: [], id: null });
  };

  const handleSaveFeed = index => {
    const feed = feeds[index];

    if (!accountId || !campaignId) {
      return;
    }

    const feedToSave = { ...feed };
    feedToSave.profile_tag_ids = [];

    // Include the local feed name if it exists
    if (localFeedNames[feed.id]) {
      feedToSave.name = localFeedNames[feed.id];

      // Clear the local feed name after saving
      setLocalFeedNames(prev => {
        const newState = { ...prev };
        delete newState[feed.id];
        return newState;
      });
    }

    if (feedToSave.profile_ids && feedToSave.profile_ids.length) {
      feedToSave.profile_ids.forEach(profileId => {
        const profile = profiles.find(p => profileId === p.id);
        if (profile && profile.es_tag_id) {
          feedToSave.profile_tag_ids.push(profile.es_tag_id);
        }
      });
      feedToSave.name = feedToSave.name.trim();
    }

    updateFeed({ accountId, campaignId, feed: feedToSave });
  };

  const handleShowDeleteConfirmation = index => {
    setFeedToDeleteIndex(index);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = () => {
    const index = feedToDeleteIndex;
    const feed = feeds[index];

    if (feed.id) {
      deleteFeed({ accountId, campaignId, feedId: feed.id });
    } else {
      deleteFeed({ feedId: null, index });
    }
    originalFeedsRef.current = originalFeedsRef.current.filter((_, i) => i !== index);
    setShowDeleteConfirmation(false);
    setFeedToDeleteIndex(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setFeedToDeleteIndex(null);
  };

  const handleCancelChanges = index => {
    if (index >= originalFeedsRef.current.length || index >= feeds.length) {
      return;
    }

    const originalFeed = originalFeedsRef.current[index];
    updateFeedProfiles({ feedIndex: index, profiles: originalFeed.profile_ids, name: originalFeed.name });

    setBanner({
      message: 'Changes cancelled successfully',
      title: 'Success',
      type: 'success'
    });
  };

  const clearBanner = () => {
    setBanner({ message: '', title: '', type: '' });
  };

  const isFeedModified = index => {
    if (index >= originalFeedsRef.current.length || index >= feeds.length) {
      return false;
    }

    const originalFeed = originalFeedsRef.current[index];
    const currentFeed = feeds[index];

    const profilesChanged = !isEqual(originalFeed.profile_ids, currentFeed.profile_ids);
    const nameChanged = currentFeed.name !== originalFeed.name;

    return profilesChanged || (nameChanged && currentFeed.name.trim().length);
  };

  if (fetchingFeeds || fetchingCampaign || fetchingCuratedFeeds) {
    return (
      <GridContent style={{ minHeight: 160, position: 'relative' }}>
        <Grid fluid>
          <Loading />
        </Grid>
      </GridContent>
    );
  }

  const disableButtons = creatingFeed || updatingFeed || deletingFeed || updatingCampaign;

  const deleteConfirmationModal = (
    <Modal isOpen={showDeleteConfirmation} onBackdropClick={handleCancelDelete} size="medium">
      <div>
        <ModalHeader onClose={handleCancelDelete}>Confirm Delete</ModalHeader>
        <ModalBody>
          <div style={{ padding: '20px' }}>
            <p>Are you sure you want to delete this feed?</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup align="right">
            <Button buttonStyle="secondary" onClick={handleCancelDelete}>
              Cancel
            </Button>
            <Button buttonStyle="negative" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </div>
    </Modal>
  );

  return (
    <GridContent style={{ minHeight: 160, position: 'relative' }}>
      {deleteConfirmationModal}
      <Grid fluid>
        <Row>
          <Col xs={12}>
            <Heading>Narrative Feeds</Heading>
            <hr />
          </Col>
        </Row>
        {bannerMessage && bannerType ? (
          <Row style={{ marginBottom: '2rem' }}>
            <Banner bannerStyle="inline" bannerType={bannerType} onClick={clearBanner} title={bannerTitle}>
              {bannerMessage}
            </Banner>
          </Row>
        ) : null}
        {isInternalAdmin && (
          <Row className={`${styles.narrativeFeedRow} well`}>
            <div className={styles.narrativeFeedField}>
              <Field>
                <Label>Curated Feeds</Label>
                <Select
                  isMulti
                  options={curatedFeeds}
                  value={enabledCuratedFeeds}
                  placeholder="Select curated feeds"
                  onChange={setEnabledCuratedFeeds}
                  isLoading={fetchingCuratedFeeds}
                />
              </Field>
            </div>
            <div className={styles.narrativeFeedButtonRow}>
              <ButtonGroup className={styles.narrativeFeedButtonGroup}>
                <Button
                  buttonStyle="positive"
                  className="btn btn-success"
                  type="button"
                  onClick={saveCuratedFeeds}
                  disabled={updatingCuratedFeeds}
                >
                  {updatingCuratedFeeds ? 'Saving...' : 'Save Curated Feeds for this Workspace'}
                </Button>
              </ButtonGroup>
            </div>
          </Row>
        )}
        <Row className={`${styles.narrativeFeedRow} well`}>
          <div className={styles.narrativeFeedField}>
            <Field>
              <Label>New Feed Name</Label>
              <Input
                data-cy="narrative-feed-name"
                placeholder="New Custom Feed Name"
                value={newFeedName}
                onChange={e => setNewFeedName(e.target.value)}
              />
            </Field>
          </div>
          <div className={styles.narrativeFeedButtonRow}>
            <ButtonGroup className={styles.narrativeFeedButtonGroup}>
              <Button
                buttonStyle="positive"
                className="btn btn-success"
                type="button"
                onClick={handleCreateFeed}
                disabled={disableButtons}
              >
                Start New Feed
              </Button>
            </ButtonGroup>
          </div>
        </Row>
        <hr />
        {feeds.map((feed, index) => (
          <div>
            <Row key={index} className={styles.narrativeFeedRow}>
              <form style={{ width: '50%' }}>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <Field style={{ width: '15rem', marginRight: '1rem' }}>
                    <Label>Feed Name</Label>
                    <Input value={feed.name} onChange={onNameChange(index)} />
                  </Field>
                  <Field style={{ width: '100%', minWidth: '20rem' }}>
                    <Label>Included Datasets</Label>
                    <Select
                      isMulti
                      options={profileOptions}
                      value={map(feed.profile_ids, v => find(profileOptions, { value: v }))}
                      onChange={onSelectedProfilesChange(index)}
                    />
                  </Field>
                </div>
              </form>

              <div
                style={{
                  width: '50%',
                  margin: 'auto 0 auto 2rem'
                }}
              >
                <ButtonGroup className={styles.narrativeFeedButtonGroup}>
                  <Button
                    buttonStyle="positive"
                    className="btn btn-success"
                    type="button"
                    onClick={() => handleSaveFeed(index)}
                    disabled={disableButtons || !isFeedModified(index)}
                  >
                    Save
                  </Button>
                  <Link
                    linkType="cancel"
                    // Cancel any changes and restore the original state
                    onClick={() => handleCancelChanges(index)}
                    disabled={disableButtons || !isFeedModified(index)}
                  >
                    Cancel
                  </Link>
                  <Button
                    buttonStyle="negative"
                    className="btn btn-danger"
                    type="button"
                    onClick={() => handleShowDeleteConfirmation(index)}
                    disabled={disableButtons}
                  >
                    Delete
                  </Button>
                </ButtonGroup>
              </div>
            </Row>
            <hr />
          </div>
        ))}
      </Grid>
    </GridContent>
  );
};

NarrativeFeeds.propTypes = {
  // Props from parent
  profiles: PropTypes.array.isRequired,
  accountId: PropTypes.string.isRequired,
  campaignId: PropTypes.string.isRequired,
  isInternalAdmin: PropTypes.bool,
  // State from Redux
  feeds: PropTypes.array,
  newFeedName: PropTypes.string,
  bannerMessage: PropTypes.string,
  bannerTitle: PropTypes.string,
  bannerType: PropTypes.string,
  curatedFeeds: PropTypes.array,
  enabledCuratedFeeds: PropTypes.array,
  campaign: PropTypes.object,
  fetchingFeeds: PropTypes.bool,
  creatingFeed: PropTypes.bool,
  updatingFeed: PropTypes.bool,
  deletingFeed: PropTypes.bool,
  fetchingCuratedFeeds: PropTypes.bool,
  updatingCuratedFeeds: PropTypes.bool,
  fetchingCampaign: PropTypes.bool,
  updatingCampaign: PropTypes.bool,
  // Action creators
  fetchFeeds: PropTypes.func,
  createFeed: PropTypes.func,
  updateFeed: PropTypes.func,
  deleteFeed: PropTypes.func,
  setNewFeedName: PropTypes.func,
  setBanner: PropTypes.func,
  updateFeedProfiles: PropTypes.func,
  updateFeedName: PropTypes.func,
  fetchCuratedFeeds: PropTypes.func,
  setEnabledCuratedFeeds: PropTypes.func,
  fetchCampaign: PropTypes.func,
  updateCampaign: PropTypes.func
};

export default connect(mapStateToProps, actions, mergeProps)(NarrativeFeeds);
